import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Truck, Clock, Heart, ArrowRight } from 'lucide-react';
import Testimonials from '../../components/public/Testimonials';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b78645e2-7e54-4fc5-b6dd-84c8c35b28ff/artifacts/60ni2i2y_FullLogo%20%281%29.jpg";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0405] via-[#1a0a0e] to-[#0a0405]" />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 25% 30%, rgba(252,165,165,0.15) 0%, transparent 50%), radial-gradient(circle at 75% 70%, rgba(251,191,36,0.12) 0%, transparent 50%)'
        }} />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-[11px] tracking-[0.35em] uppercase text-amber-300/80 mb-8" data-testid="hero-eyebrow">
              ✦ 24K Glow On The Go ✦ Same-Day Before 6PM · Emergency In The Hour
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-amber-50" data-testid="hero-title">
              Luxury nails, spray tans <span className="text-pink-300 italic">& beauty services</span>, brought to your door.
            </h1>
            <p className="mt-6 text-base text-amber-100/70 italic font-serif" data-testid="hero-subtitle">
              The monthly subscription for that <span className="text-pink-300">24K glow</span> — on the go.
            </p>
            <p className="mt-8 text-amber-100/80 leading-relaxed max-w-xl" data-testid="hero-description">
              One acrylic set costs $100+ at a salon. With <span className="text-amber-200 font-medium">Monthly Glow On The Go</span>, $75/month gets you a full acrylic set + <span className="text-amber-200">unlimited fill-ins</span>, and our $55 plan delivers <span className="text-amber-200">unlimited</span> patent-pending gel sets that last 7–14 days — plus AI 3D nail art on every visit, brought to your door.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/book"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-pink-300 to-pink-400 text-black font-medium hover:from-pink-200 hover:to-pink-300 transition-all shadow-2xl shadow-pink-500/30"
                data-testid="hero-cta-book"
              >
                Book Your Appointment <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-amber-700/50 text-amber-100 hover:bg-amber-900/30 transition-all"
                data-testid="hero-cta-portal"
              >
                Go to My Portal <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-8 text-[10px] tracking-[0.3em] uppercase text-amber-300/60" data-testid="hero-fineprint">
              Same-Day · Book Before 6PM · Emergency Within The Hour For An Upcharge
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-8 bg-gradient-to-br from-amber-400/20 via-pink-400/20 to-amber-400/20 blur-3xl rounded-full" />
            <img
              src={LOGO_URL}
              alt="Monthly Glow On The Go"
              className="relative w-full max-w-md mx-auto drop-shadow-[0_0_60px_rgba(251,191,36,0.3)]"
              data-testid="hero-logo"
            />

            {/* Floating spec cards */}
            <div className="absolute top-1/4 -right-4 lg:right-0 bg-black/60 backdrop-blur-md border border-amber-900/30 rounded-lg px-4 py-3 shadow-2xl" data-testid="hero-spec-1">
              <div className="text-[9px] tracking-[0.25em] uppercase text-amber-300/70">3D AI Printer</div>
              <div className="font-serif text-2xl text-amber-100 mt-1">3,400 <span className="text-base">DPI</span></div>
              <div className="text-[10px] text-amber-100/60">Patent-pending</div>
            </div>
            <div className="absolute bottom-1/4 -right-4 lg:right-0 bg-black/60 backdrop-blur-md border border-amber-900/30 rounded-lg px-4 py-3 shadow-2xl" data-testid="hero-spec-2">
              <div className="text-[9px] tracking-[0.25em] uppercase text-amber-300/70">Patent-Pending Hold</div>
              <div className="font-serif text-2xl text-amber-100 mt-1">7–14 <span className="text-base">days</span></div>
              <div className="text-[10px] text-amber-100/60">Full UV-cure gel</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="bg-black/40 border-y border-amber-900/30 py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { icon: Truck, label: 'We Come To You', sub: 'No salon traffic' },
            { icon: Clock, label: 'Same-Day Booking', sub: 'Before 6PM' },
            { icon: Sparkles, label: 'AI 3D Nail Art', sub: 'Every visit' },
            { icon: Heart, label: '$10 Nail Cash', sub: 'Refer & earn' },
          ].map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              data-testid={`value-prop-${i}`}
            >
              <p.icon className="w-8 h-8 mx-auto text-pink-300 mb-3" />
              <div className="text-sm tracking-wider uppercase text-amber-100">{p.label}</div>
              <div className="text-xs text-amber-100/50 mt-1">{p.sub}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PLANS PREVIEW */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-[10px] tracking-[0.35em] uppercase text-amber-300/70 mb-4" data-testid="plans-eyebrow">✦ Subscriptions ✦</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-amber-50" data-testid="plans-title">
              Get that <span className="italic text-pink-300">24-Karat</span> Glow
            </h2>
            <p className="mt-4 text-amber-100/60 max-w-xl mx-auto">
              Monthly subscriptions delivered to your door. Cancel anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Gel Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl bg-gradient-to-b from-amber-950/30 to-black/60 border border-amber-900/30 p-8 hover:border-pink-400/30 transition-all"
              data-testid="plan-card-gel"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/70">Most Popular</div>
              <div className="font-serif text-3xl text-amber-100 mt-2">Gel Glow</div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-serif text-5xl text-amber-50">$55</span>
                <span className="text-amber-100/60">/ month</span>
              </div>
              <ul className="mt-8 space-y-3 text-sm text-amber-100/80">
                {['Unlimited gel sets', '7–14 day patent-pending hold', 'AI 3D nail art on every visit', 'Mobile service to your door', 'Priority booking'].map(t => (
                  <li key={t} className="flex items-start gap-2"><span className="text-pink-300">✦</span>{t}</li>
                ))}
              </ul>
              <Link to="/plans" className="mt-8 block text-center py-3 rounded-full bg-pink-300 text-black font-medium hover:bg-pink-200 transition-all" data-testid="plan-cta-gel">
                Subscribe
              </Link>
            </motion.div>

            {/* Acrylic Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              viewport={{ once: true }}
              className="relative rounded-2xl bg-gradient-to-b from-pink-950/30 to-black/60 border border-amber-900/30 p-8 hover:border-pink-400/30 transition-all"
              data-testid="plan-card-acrylic"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/70">Best Value</div>
              <div className="font-serif text-3xl text-amber-100 mt-2">Acrylic Glow</div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-serif text-5xl text-amber-50">$75</span>
                <span className="text-amber-100/60">/ month</span>
              </div>
              <ul className="mt-8 space-y-3 text-sm text-amber-100/80">
                {['Full acrylic set every month', 'Unlimited fill-ins', 'AI 3D nail art on every visit', 'Mobile service to your door', 'VIP priority booking'].map(t => (
                  <li key={t} className="flex items-start gap-2"><span className="text-pink-300">✦</span>{t}</li>
                ))}
              </ul>
              <Link to="/plans" className="mt-8 block text-center py-3 rounded-full bg-pink-300 text-black font-medium hover:bg-pink-200 transition-all" data-testid="plan-cta-acrylic">
                Subscribe
              </Link>
            </motion.div>
          </div>

          <div className="text-center mt-10">
            <Link to="/plans" className="text-sm text-amber-200/80 hover:text-amber-100 tracking-wider uppercase" data-testid="see-all-plans">
              See full plan details →
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* BIG CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl text-amber-50" data-testid="big-cta-title">
            Ready to <span className="italic text-pink-300">glow?</span>
          </h2>
          <p className="mt-4 text-amber-100/70 max-w-xl mx-auto">
            Book your first appointment today. Serving all of Northeast Florida and Southern Georgia.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="px-10 py-4 rounded-full bg-pink-300 text-black font-medium hover:bg-pink-200 transition-all shadow-2xl shadow-pink-500/30" data-testid="big-cta-book">
              Book Appointment
            </Link>
            <Link to="/nail-cash" className="px-10 py-4 rounded-full border border-amber-700/50 text-amber-100 hover:bg-amber-900/30 transition-all" data-testid="big-cta-nailcash">
              Claim $10 Nail Cash
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

